import { useEffect, useState } from "react";
import {
    Dialog, DialogTitle, DialogContent,
    Button, Box,
    Typography,
    Paper,
    IconButton,
    DialogActions,
    CircularProgress,
    Checkbox
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { getAllCardForCustomTab } from "../../../services/hospital.service";
import { DEFAULT_CARD_FILTER } from "../../../store/reducers/hospital.slice";
import { DEFAULT_CARD_FILTER as DEFAULT_CARD_FILTER_ADMISSION } from "../../../store/reducers/admission.slice";
import { updateFilterListData } from "../../../utilis/hospital-common";
import { updateFilterListData as updateFilterListDataCommunity } from "../../../utilis/community-common";
import { updateFilterListData as updateFilterListDataDeceased } from "../../../utilis/deceased-common";
import { DEFAULT_COMMUNITY_DATA } from "../../../store/reducers/community-transfer.slice";
import { getAllCardForCommunity } from "../../../services/community-transfer.service";
import { getAllCardForDeceased } from "../../../services/deceased.service";
import { DEFAULT_DECEASED_DATA } from "../../../store/reducers/deceased.slice";
import { getAllCardForAdmissions } from "../../../services/admission.service";
import { updateFilterListDataBoth } from "../../../utilis/admission-common";
import { HOSPITAL_CARDS_LABELS, HOSPITAL_CARDS_TYPE } from "../../../types/hospital.type";
import { ADMISSION_CARDS_LABELS, ADMISSION_CARDS_TYPE } from "../../../types/admission.type";
import { CO_TRANSFER_CARDS_TYPE, COMMUNITY_CARD_LABELS } from "../../../types/community-transfer.type";
import { DECEASED_CARDS_LABELS, DECEASED_CARDS_TYPE } from "../../../types/deceased.type";
import { createCustomTab, getDynamicDataTabById } from "../../../services/custom-tab.service";
import { useDispatch, useSelector } from "react-redux";
import { ADD_NOTIFICATION } from "../../../store/types";
import { getQuestionsData } from "../../../services/dynamic-data-tab.service";
import { ADT_SUB_TYPES } from "../../../types/common.type";
import { setIsCustomTabAdded } from "../../../store/reducers/common.slice";
import moment from "moment";
import ErrorPopover from '../custom-popover/ErrorPopover';
import LabeledSelect from './LabeledSelect';
import LabeledTextField from './LabeledTextField';
import LabeledTextarea from './LabeledTextarea';
import DialogActionsRow from './DialogActionsRow';
import ItemAutocomplete from "./ItemAutocomplete";
import { selectedTabAccess } from "../../../utilis/common";
import { PAGE_TYPE } from "../../../types/pages.type";
import aiService from "../../../services/api/ai.service";

const validPageTypes = Object.entries(PAGE_TYPE).filter(
    ([key]) => key !== "OVERALL" && key !== "ADT"
);

const CARD_CONFIG = {
    [PAGE_TYPE.HOSPITAL]: [
        { value: HOSPITAL_CARDS_TYPE.TOTAL, label: HOSPITAL_CARDS_LABELS.total },
        { value: HOSPITAL_CARDS_TYPE.UNPLANNED, label: HOSPITAL_CARDS_LABELS.unplannedHospitalTransfer },
        { value: HOSPITAL_CARDS_TYPE.PLANNED, label: HOSPITAL_CARDS_LABELS.plannedHospitalTransfer },
    ],
    [PAGE_TYPE.COMMUNITY_TRANSFER]: [
        { value: CO_TRANSFER_CARDS_TYPE.TOTAL, label: COMMUNITY_CARD_LABELS.total },
        { value: CO_TRANSFER_CARDS_TYPE.AMA, label: COMMUNITY_CARD_LABELS.AMA },
        { value: CO_TRANSFER_CARDS_TYPE.SNF, label: COMMUNITY_CARD_LABELS.SNF },
        { value: CO_TRANSFER_CARDS_TYPE.SAFE_DISCHARGE, label: COMMUNITY_CARD_LABELS.safeDischarge },
    ],
    [PAGE_TYPE.DECEASED]: [
        { value: DECEASED_CARDS_TYPE.TOTAL, label: DECEASED_CARDS_LABELS.total },
    ],
    [PAGE_TYPE.ADMISSION]: [
        { value: ADMISSION_CARDS_TYPE.TOTAL, label: ADMISSION_CARDS_LABELS.total },
        { value: ADMISSION_CARDS_TYPE.ADMISSION, label: ADMISSION_CARDS_LABELS.admission },
        { value: ADMISSION_CARDS_TYPE.READMISSION, label: ADMISSION_CARDS_LABELS.readmission },
    ],
};

const PAGE_CONFIG = {
    [PAGE_TYPE.HOSPITAL]: {
        cardFilter: DEFAULT_CARD_FILTER,
        getCards: getAllCardForCustomTab,
        updateFn: updateFilterListData,
        query: {
            forType: "transfer",
            forTransferType: ["hospitalTransfer"],
            isCustom: true,
        },
    },
    [PAGE_TYPE.COMMUNITY_TRANSFER]: {
        cardFilter: DEFAULT_COMMUNITY_DATA,
        getCards: getAllCardForCommunity,
        updateFn: updateFilterListDataCommunity,
        query: {
            forType: "transfer",
            forTransferType: [ADT_SUB_TYPES.AMA, ADT_SUB_TYPES.SAFE_DISCHARGE, ADT_SUB_TYPES.SNF],
            isCustom: true,
        },
    },
    [PAGE_TYPE.DECEASED]: {
        cardFilter: DEFAULT_DECEASED_DATA,
        getCards: getAllCardForDeceased,
        updateFn: updateFilterListDataDeceased,
        query: {
            forType: "transfer",
            forTransferType: ["deceased"],
            isCustom: true,
        },
    },
    [PAGE_TYPE.ADMISSION]: {
        cardFilter: DEFAULT_CARD_FILTER_ADMISSION,
        getCards: getAllCardForAdmissions,
        updateFn: updateFilterListDataBoth,
        query: {
            isCustom: true,
        },
    },
};

const LABEL_MAP = {
    [PAGE_TYPE.ADMISSION]: ADMISSION_CARDS_LABELS,
    [PAGE_TYPE.COMMUNITY_TRANSFER]: COMMUNITY_CARD_LABELS,
    [PAGE_TYPE.DECEASED]: DECEASED_CARDS_LABELS,
    [PAGE_TYPE.HOSPITAL]: HOSPITAL_CARDS_LABELS,
};

const AddCustomTabDialog = ({ handleClose, filter, page, id = null, isCreator: isCreatorProp = true, existingTabOptions = [] }) => {
    const selectedTab = selectedTabAccess();
    const isEditMode = Boolean(id);
    const [dataPointCount, setDataPointCount] = useState(1);
    const [inputValues, setInputValues] = useState([""]);
    const [cardItemData, setCardItemData] = useState([]);
    const activeFacilities = useSelector((state) => state.activeFacilities.facilities);
    const [selectedCardItems, setSelectedCardItems] = useState([
        {
            filters: [
                {
                    card: { value: "", label: "", questionId: null },
                    items: [],
                    isMainCard: false
                }
            ],
            dashboard: ""
        }
    ]);
    const [cachedData, setCachedData] = useState({});
    const [errorPopover, setErrorPopover] = useState({ open: false, message: "" });
    const dispatch = useDispatch();
    const currentUser = useSelector((state) => state.auth);
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [errorMessages, setErrorMessages] = useState([]);
    const [fieldErrors, setFieldErrors] = useState({
        title: false,
        dataPoints: [], // For each data point
    });
    const [isSaving, setIsSaving] = useState(false);
    const [titleError, setTitleError] = useState(false);
    const [isCreator, setIsCreator] = useState(isCreatorProp); // Initialize with prop
    // const [isLoading, setIsLoading] = useState(true);
    const [focusDataPointIndex, setFocusDataPointIndex] = useState(null); // For same-type data points toggle
    const [initialLoading, setInitialLoading] = useState(isEditMode);

    // AI Description states
    const [isVerifyingAI, setIsVerifyingAI] = useState(false);
    const [aiDescription, setAiDescription] = useState("");
    const [aiDescriptionError, setAiDescriptionError] = useState(null);

    useEffect(() => {

        if (isEditMode && id) {
            setInitialLoading(true);
            const fetchTabDetails = async () => {
                try {
                    // setIsLoading(true);
                    // Get the tab details
                    const tabData = await getDynamicDataTabById(id);
                    if (tabData) {
                        // Use the prop instead of checking
                        setIsCreator(isCreatorProp);

                        // Pre-fill the form data
                        setTitle(tabData.title || "");
                        setDescription(tabData.description || "");
                        setAiDescription(tabData.aiDescription || ""); // Load AI description if exists
                        const parsedFocusIndex = tabData.focusDataPointIndex ?? null;
                        const focusIndexNumber = parsedFocusIndex === null ? null : Number(parsedFocusIndex);
                        setFocusDataPointIndex(Number.isNaN(focusIndexNumber) ? null : focusIndexNumber); // Load focus data point index as number

                        const filters = tabData.filters || [];
                        const filterCount = filters.length || 1;
                        setDataPointCount(filterCount);

                        // Initialize arrays with proper length
                        const inputValuesEdit = new Array(filterCount).fill("");
                        const newCachedData = {};
                        const pendingFilters = new Array(filterCount);
                        const pendingCardItemData = new Array(filterCount);

                        // First, fetch all card data
                        await Promise.all(
                            filters.map(async (filterGroup, index) => {
                                const dashboard = filterGroup.dashboard;
                                inputValuesEdit[index] = dashboard;

                                let result = cachedData[dashboard];
                                if (!result) {
                                    result = await fetchCardAndItemOptions(dashboard);
                                    newCachedData[dashboard] = result;
                                }

                                pendingCardItemData[index] = { loading: false, ...result };

                                const filtersArray = (filterGroup.filters || []).map((filter, filterIndex) => {
                                    if (!filter.card?.value && filter.card?.label) {
                                        filter.card.value = filter.card.label;
                                    }

                                    return {
                                        card: filter.card,
                                        items: filter.items || [],
                                        isMainCard:
                                            index === filters.length - 1 &&
                                            filterIndex === (filterGroup.filters.length - 1),
                                    };
                                });

                                pendingFilters[index] = {
                                    dashboard,
                                    filters: filtersArray,
                                };
                            })
                        );

                        // Set all states at once to avoid race conditions
                        setInputValues(inputValuesEdit);
                        setCardItemData(pendingCardItemData);
                        setSelectedCardItems(pendingFilters);
                        setCachedData((prevCache) => ({ ...prevCache, ...newCachedData }));
                    }
                } catch (error) {
                    console.error("Error fetching tab data:", error);
                    dispatch({
                        type: ADD_NOTIFICATION,
                        payload: {
                            type: "error",
                            label: "Failed to load tab details",
                            id: "tabLoadError",
                        },
                    });
                } finally {
                    // setIsLoading(false);
                    setInitialLoading(false);
                }
            };
            fetchTabDetails();
        } else {
            // For new tabs, use the prop value
            setIsCreator(isCreatorProp);
            // setIsLoading(false);
            setInitialLoading(false);
        }
    }, [id, isEditMode, currentUser, dispatch, isCreatorProp]); // eslint-disable-line react-hooks/exhaustive-deps

    // Ensure cardItemData array length matches dataPointCount
    useEffect(() => {
        if (cardItemData.length !== dataPointCount) {
            setCardItemData((prev) => {
                const updated = [...prev];
                if (dataPointCount > prev.length) {
                    // Add missing elements
                    for (let i = prev.length; i < dataPointCount; i++) {
                        updated[i] = { loading: false, cards: [], items: [] };
                    }
                } else {
                    // Remove extra elements
                    updated.splice(dataPointCount);
                }
                return updated;
            });
        }
    }, [dataPointCount, cardItemData.length]);

    // Ensure cardItemData is loaded for all selected dashboards
    useEffect(() => {
        const loadMissingData = async () => {
            const promises = [];

            selectedCardItems.forEach((item, index) => {
                if (item?.dashboard && !cardItemData[index]?.cards?.length && !cardItemData[index]?.loading) {
                    promises.push(
                        (async () => {
                            setCardItemData((prev) => {
                                const updated = [...prev];
                                updated[index] = { ...updated[index], loading: true };
                                return updated;
                            });

                            const result = await fetchCardAndItemOptions(item.dashboard);
                            setCachedData((prevCache) => ({ ...prevCache, [item.dashboard]: result }));

                            setCardItemData((prev) => {
                                const updated = [...prev];
                                updated[index] = { loading: false, ...result };
                                return updated;
                            });
                        })()
                    );
                }
            });

            if (promises.length > 0) {
                await Promise.all(promises);
            }
        };

        if (selectedCardItems.length > 0) {
            loadMissingData();
        }
    }, [selectedCardItems.map(item => item?.dashboard).join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

    async function fetchCardAndItemOptions(dataPoint) {
        const cards = [];
        const items = [];

        const config = PAGE_CONFIG[dataPoint];


        if (!config) return { cards, items };

        const updatedFilter = {
            ...filter,
            facilityIds: activeFacilities,
            isSkipCustomCard: true,
            ...(filter?.startDate && filter?.endDate
                ? (() => {
                    const startDate = moment(filter.startDate);
                    const endDate = moment(filter.endDate);
                    const diffDays = endDate.diff(startDate, 'days');

                    return {
                        startDate: diffDays < 30 ? startDate.subtract(30, "days").toDate() : startDate.toDate(),
                        // startDate: filter.startDate,
                        endDate: filter.endDate,
                    };
                })()
                : {}),
        };

        const res = await config.getCards(updatedFilter);

        const query = { page, ...config.query };
        const dynamicCardsData = await getQuestionsData(query);
        const dynamicCardsDataArr = dynamicCardsData?.filter(item => (!item.isDefaultCard)) ?? [];
        const dynamicCards = dynamicCardsDataArr?.filter(item => (!item.existingAccessor)) ?? [];

        const labelMap = LABEL_MAP[dataPoint] ?? {};
        const cardFilter = config.cardFilter;

        let result;

        if (dataPoint === PAGE_TYPE.ADMISSION) {
            delete cardFilter.averageStayDaysData;

            result = await config.updateFn(
                { ...res?.data, dynamicCards },
                cardFilter,
                null,
                cardFilter?.mainPriorityData,
                [],
                false,
            );

            if (result?.patientList?.averageStayDaysData) {
                delete result.patientList.averageStayDaysData;
            }

        } else if (dataPoint === PAGE_TYPE.HOSPITAL) {
            result = await config.updateFn(
                cardFilter,
                null,
                { ...res, dynamicCards },
                cardFilter?.priorityData,
                false,
                res?.defaultCardShiftData ? [res?.defaultCardShiftData] : []
            );

        } else if (dataPoint === PAGE_TYPE.DECEASED) {
            // Deceased: (cardFilter, patientList, priorityData, forComparison) — do not pass transferType placeholder.
            result = await config.updateFn(
                cardFilter,
                { ...res?.data, dynamicCards },
                cardFilter?.priorityData,
                false
            );
        } else {
            result = await config.updateFn(
                cardFilter,
                { ...res?.data, dynamicCards },
                cardFilter?.priorityData,
                [],
                false
            );
        }

        const latestListData = dataPoint === PAGE_TYPE.ADMISSION ? result?.patientList : result;
        if (!latestListData) return { cards, items };

        for (const [key, value] of Object.entries(latestListData)) {
            if (key === "pageConverterFilteredPatients") continue;
            if (!Array.isArray(value) || /_?customtab/i.test(key)) continue;

            const dynamicCard = dynamicCards.find(dc => dc.accessor === key);
            const label = dynamicCard?.label ?? dynamicCard?.tableLabel ?? labelMap[key] ?? key;

            cards.push({
                value: key,
                label,
                questionId: dynamicCard?._id ?? null,
                dataPoint,
            });

            for (const ele of value) {
                if (ele?.isSpacialItem) continue;

                items.push({
                    value: ele._id,
                    label: ele?.label ?? ele?.name ?? ele?.title,
                    parentId: key,
                    questionId: dynamicCard?._id ?? null,
                });
            }
        }

        if (CARD_CONFIG[dataPoint]) {
            const staticCards = CARD_CONFIG[dataPoint].map(card => ({
                ...card,
                questionId: null,
                dataPoint,
                isTotalCard: true,
            }));
            cards.unshift(...staticCards);
        }

        return { cards, items };
    }

    const handleCountChange = (e) => {
        const value = e.target.value;

        // Allow clearing the input (intermediate empty state)
        if (value === "") {
            setDataPointCount("");
            return;
        }

        const newCount = parseInt(value);
        if (isNaN(newCount) || newCount < 0) return;

        setDataPointCount(newCount);

        setInputValues((prev) =>
            newCount > prev.length ? [...prev, ...Array(newCount - prev.length).fill("")] : prev.slice(0, newCount)
        );

        setCardItemData((prev) =>
            newCount > prev.length
                ? [...prev, ...Array(newCount - prev.length).fill({ loading: false, cards: [], items: [] })]
                : prev.slice(0, newCount)
        );

        setSelectedCardItems((prev) =>
            newCount > prev.length
                ? [...prev, ...Array(newCount - prev.length).fill({
                    filters: [{ card: { value: "", label: "", questionId: null }, items: [], isMainCard: false }],
                    dashboard: ""
                })]
                : prev.slice(0, newCount)
        );
    };

    const handleInputChange = async (index, value) => {
        // Update input values
        const updatedInputs = [...inputValues];
        updatedInputs[index] = value;

        setInputValues(updatedInputs);

        // Reset the selected filters for this data point because the page type has changed
        setSelectedCardItems((prev) => {
            const updated = [...prev];
            updated[index] = {
                filters: [{ items: [], card: { value: "", label: "", questionId: null }, isMainCard: false }],
                dashboard: value,
            };
            return updated;
        });

        // Use cached if exists
        if (cachedData[value]) {
            setCardItemData((prev) => {
                const updated = [...prev];
                updated[index] = { loading: false, ...cachedData[value] };
                return updated;
            });
            return;
        }

        // Set loading while fetching
        setCardItemData((prev) => {
            const updated = [...prev];
            updated[index] = { loading: true, cards: [], items: [] };
            return updated;
        });

        // Fetch and set new data
        const result = await fetchCardAndItemOptions(value);
        setCachedData((prevCache) => ({ ...prevCache, [value]: result }));

        setCardItemData((prev) => {
            const updated = [...prev];
            updated[index] = { loading: false, ...result };
            return updated;
        });
    };


    const handleCardItemChange = (dpIndex, filterIndex, field, value) => {
        const isMainCard = dpIndex === dataPointCount - 1 && filterIndex === (selectedCardItems[dpIndex]?.filters.length - 1);

        setSelectedCardItems((prev) => {
            const updated = [...prev];

            // Ensure the data point exists
            const existingDataPoint = updated[dpIndex] || {
                filters: [],
                dashboard: inputValues[dpIndex],
            };

            // Ensure filters array is properly cloned
            const filters = [...(existingDataPoint.filters || [])];

            // Ensure the filter at filterIndex exists with safe defaults
            filters[filterIndex] = filters[filterIndex] || {
                card: { value: "", label: "", questionId: null },
                items: [],
                isMainCard,
            };

            if (field === "card") {
                filters[filterIndex] = {
                    ...filters[filterIndex],
                    card: value,
                    items: [], // Reset items when changing card
                    isMainCard,
                };
            } else if (field === "items") {
                filters[filterIndex] = {
                    ...filters[filterIndex],
                    items: value,
                    isMainCard,
                };
            }

            // Assign the updated filters back to the data point
            existingDataPoint.filters = filters;
            existingDataPoint.dashboard = inputValues[dpIndex]; // Ensure dashboard always syncs

            updated[dpIndex] = existingDataPoint;
            return updated;
        });
    };

    const handleConfirm = async () => {
        const errors = [];
        const newFieldErrors = {
            title: false,
            dataPoints: [],
        };

        if (dataPointCount < 1) {
            errors.push("At least one data point is required.");
        }

        // Check for duplicate data points matching the current page and validate focus selection
        const sameTypeDataPointsOnPage = getSameTypeDataPointsForPage();

        if (sameTypeDataPointsOnPage.length > 0) {
            const selectedGroups = sameTypeDataPointsOnPage.filter(group =>
                group.indices.some(index => focusDataPointIndex === index)
            );

            if (selectedGroups.length !== sameTypeDataPointsOnPage.length) {
                const missingGroups = sameTypeDataPointsOnPage.filter(group =>
                    !group.indices.some(index => focusDataPointIndex === index)
                );
                const missingTypes = missingGroups.map(group =>
                    group.type.charAt(0).toUpperCase() + group.type.slice(1).toLowerCase()
                ).join(', ');
                errors.push(`Please select a primary data point for: ${missingTypes}`);
            }
        }

        const selectedDataPoints = inputValues.slice(0, dataPointCount).filter(Boolean);

        // Validate that at least one data point matches the current page
        const hasMatchingDataPoint = selectedDataPoints.some(dataPoint => dataPoint === page);

        if (!hasMatchingDataPoint) {
            errors.push(`At least one data point must match the current dashboard (${page}).`);
            // Mark all data points as having dashboard errors
            for (let i = 0; i < dataPointCount; i++) {
                if (!newFieldErrors.dataPoints[i]) {
                    newFieldErrors.dataPoints[i] = { dashboard: false, filters: [] };
                }
                newFieldErrors.dataPoints[i].dashboard = true;
            }
        }

        for (let i = 0; i < dataPointCount; i++) {
            let dataPointError = {
                dashboard: false,
                filters: [],
            };

            if (!selectedCardItems[i]?.dashboard) {
                errors.push(`Please select a data point for Data Point ${i + 1}`);
                dataPointError.dashboard = true;
            }

            const filters = selectedCardItems[i]?.filters || [];

            filters.forEach((filter, filterIndex) => {
                let filterError = {
                    card: false,
                    items: false,
                };

                if (!filter?.card?.value) {
                    errors.push(`Please select a card for Filter ${filterIndex + 1} in Data Point ${i + 1}`);
                    filterError.card = true;
                }

                if (filter.items.length === 0) {
                    if (!filter?.card?.isTotalCard && !filter.isMainCard) {
                        errors.push(`Please select at least one item for Filter ${filterIndex + 1} in Data Point ${i + 1}`);
                        filterError.items = true;
                    }
                }

                dataPointError.filters.push(filterError);
            });

            newFieldErrors.dataPoints.push(dataPointError);
        }

        const normalizedTitle = title.trim().toLowerCase();
        const currentId = id ? String(id) : null;
        const hasDuplicateTitle = normalizedTitle
            ? existingTabOptions.some((tab) => {
                const tabTitle = (tab?.label ?? tab?.title ?? "").trim().toLowerCase();
                if (!tabTitle || tabTitle !== normalizedTitle) return false;
                const tabId = tab?._id ? String(tab._id) : null;
                if (isEditMode && currentId && tabId && tabId === currentId) return false;
                return true;
            })
            : false;

        if (!title.trim()) {
            errors.push("Title is required.");
            newFieldErrors.title = true;
        } else if (hasDuplicateTitle) {
            errors.push("Title already exists.");
            newFieldErrors.title = true;
        }

        if (errors.length > 0) {
            setErrorMessages(errors);
            setErrorPopover({ open: true });
            setFieldErrors(newFieldErrors);
            setTitleError(newFieldErrors.title);
            return;
        }

        setIsSaving(true);
        try {
            // Handle focus selection only when duplicate data points match the current page
            const sameTypeDataPointsOnPage = getSameTypeDataPointsForPage();

            let effectiveFocusDataPointIndex = null;
            if (sameTypeDataPointsOnPage.length > 0) {
                effectiveFocusDataPointIndex = focusDataPointIndex !== null ? Number(focusDataPointIndex) : null;
            }

            let res = await createCustomTab({
                page,
                filters: selectedCardItems,
                title: title.trim(),
                description: description.trim(),
                aiDescription: aiDescription.trim(),
                focusDataPointIndex: effectiveFocusDataPointIndex,
                id,
                isEditMode
            });

            if (res?.status === 403) {
                errors.push(res.message);
                setErrorMessages(errors);
                setErrorPopover({ open: true });
                return;
            }

            if (res?.status === 404) {
                errors.push(res.message);
                setErrorMessages(errors);
                setErrorPopover({ open: true });
                return;
            }

            if (res?.status === 400) {
                if (!title.trim()) {
                    errors.push("Title is required.");
                } else if (hasDuplicateTitle) {
                    errors.push("Title already exists.");
                } else {
                    errors.push(res.message);
                }
                setErrorMessages(errors);
                setErrorPopover({ open: true });
                setTitleError(true);
                return;
            }

            if (res?.status === 500) {
                errors.push(res.message);
                setErrorMessages(errors);
                setErrorPopover({ open: true });
                return;
            }

            if (res) {
                dispatch({
                    type: ADD_NOTIFICATION,
                    payload: {
                        type: "success",
                        label: isEditMode ? "Custom tab updated successfully!" : "Custom tab added successfully!",
                        id: "customTabAdded",
                    },
                });
            }
            // Force a consistent refresh pulse for every save:
            // pages currently refetch when this flag becomes true.
            dispatch(setIsCustomTabAdded(true));
            window.setTimeout(() => {
                dispatch(setIsCustomTabAdded(false));
            }, 0);
            handleClose();
        } catch (error) {
            console.error('Error saving custom tab:', error);
            dispatch({
                type: ADD_NOTIFICATION,
                payload: {
                    type: "error",
                    label: "Failed to save custom tab. Please try again.",
                    id: "customTabError",
                },
            });
        } finally {
            setIsSaving(false);
        }
    };

    const addMoreCardItem = (dpIndex) => {
        setSelectedCardItems((prev) => {
            const updated = [...prev];

            // Ensure existing data point
            const existingDataPoint = updated[dpIndex] || { filters: [], dashboard: inputValues[dpIndex] };

            existingDataPoint.filters = [...(existingDataPoint.filters || [])];

            // Add new filter
            existingDataPoint.filters.push({ card: { value: "", label: "", questionId: null }, items: [], isMainCard: false });

            // After adding, loop through all filters and set isMainCard only for last filter of last DP
            updated.forEach((dp, dpI) => {
                if (!dp) return;
                dp.filters = dp.filters.map((filter, filterI) => ({
                    ...filter,
                    isMainCard: dpI === dataPointCount - 1 && filterI === (dp.filters.length - 1),
                }));
            });

            updated[dpIndex] = existingDataPoint;
            return updated;
        });
    };

    const handleRemoveFilter = (cardIndex, filterIndex) => {
        const updated = [...selectedCardItems];
        updated[cardIndex].filters = updated[cardIndex].filters.filter((_, i) => i !== filterIndex);
        setSelectedCardItems(updated); // Assuming you're using useState
    };

    // Add new function to check if any data point is loading
    const isAnyDataPointLoading = () => {
        return initialLoading || cardItemData.some(data => data?.loading);
    };

    // Add handler for title change to clear error
    const handleTitleChange = (e) => {
        setTitle(e.target.value);
        if (titleError) {
            setTitleError(false);
        }
    };

    // Function to get the data points that are the same
    const getSameTypeDataPoints = () => {
        const dataPointCounts = {};

        // Count occurrences by original index to preserve positions
        for (let i = 0; i < dataPointCount; i++) {
            const dataPoint = inputValues[i];
            if (!dataPoint) continue;

            if (!dataPointCounts[dataPoint]) {
                dataPointCounts[dataPoint] = [];
            }
            dataPointCounts[dataPoint].push(i);
        }

        // Find data points that appear more than once
        const sameTypeDataPoints = [];
        Object.entries(dataPointCounts).forEach(([dataPoint, indices]) => {
            if (indices.length > 1) {
                sameTypeDataPoints.push({
                    type: dataPoint,
                    indices,
                });
            }
        });

        return sameTypeDataPoints;
    };

    // Duplicate groups where the dashboard matches the current page
    const getSameTypeDataPointsForPage = () => {
        return getSameTypeDataPoints().filter(group => group.type === page);
    };

    // Show focus UI only when this index is part of a duplicate group on the current page
    const hasSameTypeForDataPoint = (dataPointIndex) => {
        const sameTypeDataPoints = getSameTypeDataPointsForPage();
        return sameTypeDataPoints.some(group => group.indices.includes(dataPointIndex));
    };

    // Function to check if a specific data point index is selected as focus for its group
    const isSelectedAsFocusForGroup = (dataPointIndex) => {
        // Ensure both values are numbers for comparison
        const focusIndex = focusDataPointIndex !== null ? Number(focusDataPointIndex) : null;
        const dataIndex = Number(dataPointIndex);

        if (focusIndex === null) return false;

        return focusIndex === dataIndex;
    };

    // Function to handle checkbox selection for a specific data point
    const handleFocusSelection = (dataPointIndex) => {
        const dataIndex = Number(dataPointIndex);
        // Only allow selection when there are same-type data points
        if (!hasSameTypeForDataPoint(dataIndex)) return;

        // Ensure both values are numbers for comparison
        const currentFocusIndex = focusDataPointIndex !== null ? Number(focusDataPointIndex) : null;

        // If this data point is already selected, deselect it
        if (currentFocusIndex === dataIndex) {
            setFocusDataPointIndex(null);
        } else {
            // Select this data point as focus for its group
            setFocusDataPointIndex(dataIndex);
        }
    };

    // AI Description verification handler
    const handleVerifyAIDescription = async () => {
        if (!description || !description.trim()) {
            return;
        }

        try {
            setIsVerifyingAI(true);
            setAiDescriptionError(null);

            const result = await aiService.verifyAIDescription(
                description,
                title,
                'customTab'
            );

            // Save the AI description
            setAiDescription(result.aiUnderstanding);

        } catch (error) {
            console.error('Error verifying AI description:', error);
            setAiDescriptionError(error.message || 'Failed to verify AI description');
        } finally {
            setIsVerifyingAI(false);
        }
    };

    return (
        <>
            <ErrorPopover
                open={errorPopover.open}
                onClose={() => setErrorPopover({ open: false })}
                errorMessages={errorMessages}
            />

            <Dialog open={true} onClose={initialLoading ? undefined : handleClose} maxWidth="md" fullWidth>
                <DialogTitle>
                    {isEditMode
                        ? (isCreator ? 'Edit Custom Tab' : 'View Custom Tab')
                        : 'Create Custom Tab'
                    }
                    <Typography variant="subtitle2" sx={{ mt: 0.5, color: 'text.secondary' }}>
                        Historical data (last 365 days) is included for comparison
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    {initialLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            <LabeledSelect
                                label="Number of Data Points"
                                value={dataPointCount}
                                onChange={handleCountChange}
                                options={[
                                    { label: '1', value: 1 },
                                    { label: '2', value: 2 },
                                    { label: '3', value: 3 },
                                    { label: '4', value: 4 },
                                    { label: '5', value: 5 }
                                ]}
                                error={false}
                                sx={{ mt: 2, width: 160 }}
                                size="medium"
                                disabled={!isCreator}
                            />

                            {inputValues.map((val, index) => (
                                <Paper key={index} elevation={1} sx={{ p: 2, borderRadius: 2, backgroundColor: "#fafafa" }}>
                                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                                        <Typography variant="subtitle1" gutterBottom>
                                            Data Point {index + 1}
                                        </Typography>

                                        {/* Checkbox for same-type data points */}
                                        {hasSameTypeForDataPoint(index) && dataPointCount > 1 && (
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Typography variant="body2" sx={{ color: '#856404', fontSize: '0.875rem', fontWeight: 500 }}>
                                                    {(() => {
                                                        const sameTypeDataPoints = getSameTypeDataPointsForPage();
                                                        const currentGroup = sameTypeDataPoints.find(group => group.indices.includes(index));
                                                        if (currentGroup) {
                                                            const dataPointType = currentGroup.type.charAt(0).toUpperCase() + currentGroup.type.slice(1).toLowerCase();
                                                            return `There are multiple ${dataPointType} Data Points`;
                                                        }
                                                        return '';
                                                    })()}
                                                </Typography>
                                                <Checkbox
                                                    checked={isSelectedAsFocusForGroup(index)}
                                                    onChange={() => handleFocusSelection(index)}
                                                    disabled={!isCreator}
                                                    sx={{
                                                        color: '#4879F5',
                                                        '&.Mui-checked': {
                                                            color: '#4879F5',
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        )}
                                    </Box>

                                    <LabeledSelect
                                        label="Select Data Point"
                                        value={val}
                                        onChange={(e) => handleInputChange(index, e.target.value)}
                                        options={(() => {
                                            const filteredOptions = validPageTypes.filter(([_, value]) => selectedTab.includes(value));
                                            // Allow any dashboard selection for all data points
                                            return filteredOptions.map(([label, value]) => ({
                                                label: label.charAt(0).toUpperCase() + label.slice(1).toLowerCase(),
                                                value,
                                            }));
                                        })()}
                                        error={fieldErrors?.dataPoints[index]?.dashboard}
                                        disabled={!isCreator}
                                    />

                                    {(selectedCardItems[index]?.filters || []).map((filter, filterIndex) => {
                                        const isNotMainCard = index !== dataPointCount - 1 || filterIndex !== (selectedCardItems[index]?.filters.length - 1);
                                        const isMainCard = index === dataPointCount - 1 && filterIndex === (selectedCardItems[index]?.filters.length - 1);

                                        return (
                                            <Box key={filterIndex} display="flex" gap={2} flexDirection={{ xs: "column", sm: "row" }} mt={2}>
                                                <LabeledSelect
                                                    disabled={!isCreator}
                                                    label="Select Card"
                                                    value={filter?.card?.value || ""}
                                                    onChange={(ele) => {
                                                        const selectedValue = ele.target.value;
                                                        const selectedCard = cardItemData[index]?.cards?.find((card) => card.value === selectedValue);
                                                        handleCardItemChange(index, filterIndex, "card", selectedCard, isMainCard);
                                                    }}
                                                    options={cardItemData[index]?.cards
                                                        ?.filter((card) => {
                                                            const selectedValues = (selectedCardItems[index]?.filters || [])
                                                                .filter((_, i) => i !== filterIndex)
                                                                .map((f) => f.card?.value);
                                                            return !selectedValues.includes(card.value);
                                                        })
                                                        ?.map(card => ({ label: card.label, value: card.value })) || []}
                                                    error={fieldErrors?.dataPoints[index]?.filters[filterIndex]?.card}
                                                    size="medium"
                                                />

                                                {isNotMainCard && !filter?.card?.isTotalCard && (
                                                    <ItemAutocomplete
                                                        label="Select Items"
                                                        value={Array.isArray(filter?.items) ? filter.items : []}
                                                        onChange={(e, newValue) => {
                                                            // Handled inside the component already — you can skip if you're using handleCardItemChange there
                                                        }}
                                                        error={fieldErrors?.dataPoints[index]?.filters[filterIndex]?.items}
                                                        helperText=""
                                                        cardItemData={cardItemData}
                                                        index={index}
                                                        filterIndex={filterIndex}
                                                        filter={filter}
                                                        isMainCard={isMainCard}
                                                        handleCardItemChange={handleCardItemChange}
                                                        fieldErrors={fieldErrors}
                                                        disabled={!isCreator}
                                                    />
                                                )}

                                                {selectedCardItems[index]?.filters.length > 1 && (
                                                    <IconButton
                                                        aria-label="Remove Filter"
                                                        color="error"
                                                        size="small"
                                                        onClick={() => handleRemoveFilter(index, filterIndex)}
                                                        sx={{
                                                            mt: 1,
                                                            width: 36,
                                                            height: 36,
                                                            padding: 1,
                                                            alignSelf: "flex-start", // If inside a flex, make sure it's aligned properly
                                                            "@media (max-width: 768px)": {
                                                                width: 28,
                                                                height: 28,
                                                                padding: 0.5
                                                            }
                                                        }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                            </Box>
                                        )
                                    })}

                                    {isCreator && (
                                        <Button
                                            variant="text"
                                            onClick={() => addMoreCardItem(index)}
                                            sx={{ mt: 2 }}
                                        >
                                            Add More Filter
                                        </Button>
                                    )}
                                </Paper>
                            ))}

                            <LabeledTextField
                                label="Tab Title"
                                fullWidth
                                margin="normal"
                                value={title}
                                onChange={handleTitleChange}
                                sx={{ mt: 5 }}
                                error={titleError}
                                helperText={
                                    titleError
                                        ? (errorMessages.find((msg) => /title/i.test(msg)) || "Title is required.")
                                        : ""
                                }
                                disabled={!isCreator}
                            />

                            <LabeledTextarea
                                label="Description"
                                minRows={4}
                                maxRows={8}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Enter description"
                                style={{
                                    width: '100%',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                    borderRadius: '4px',
                                    borderColor: '#ccc',
                                    resize: 'vertical',
                                }}
                                disabled={!isCreator}
                            />

                            {/* Confirm with AI Description Button */}
                            {description && description.trim() && (
                                <Box sx={{ mt: 1, mb: 2 }}>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        onClick={handleVerifyAIDescription}
                                        disabled={isVerifyingAI || !isCreator}
                                        startIcon={isVerifyingAI ? <CircularProgress size={16} /> : <CheckCircleIcon />}
                                    >
                                        {isVerifyingAI ? 'Verifying...' : 'Confirm with AI Description'}
                                    </Button>
                                </Box>
                            )}

                            {/* Display AI Description */}
                            {aiDescription && (
                                <Box
                                    sx={{
                                        mt: 2,
                                        p: 2,
                                        bgcolor: 'rgba(76, 175, 80, 0.08)',
                                        borderRadius: 1,
                                        border: '1px solid rgba(76, 175, 80, 0.3)'
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <CheckCircleIcon sx={{ color: 'success.main', mr: 1, fontSize: 20 }} />
                                        <strong>AI Description:</strong>
                                    </Box>
                                    <Box sx={{ pl: 4, color: 'text.secondary' }}>
                                        {aiDescription}
                                    </Box>
                                </Box>
                            )}

                            {/* Display Error */}
                            {aiDescriptionError && (
                                <Box
                                    sx={{
                                        mt: 2,
                                        p: 2,
                                        bgcolor: 'rgba(211, 47, 47, 0.08)',
                                        borderRadius: 1,
                                        border: '1px solid rgba(211, 47, 47, 0.3)',
                                        color: 'error.main'
                                    }}
                                >
                                    {aiDescriptionError}
                                </Box>
                            )}
                        </>
                    )}
                </DialogContent>

                {isCreator ? (
                    <DialogActionsRow
                        onCancel={handleClose}
                        onSave={handleConfirm}
                        loading={isSaving || initialLoading}
                        disabled={isSaving || (isEditMode && isAnyDataPointLoading())}
                        cancelText="Cancel"
                        saveText="Save"
                        loadingText="Saving..."
                    />
                ) : (
                    <DialogActions>
                        <Button onClick={handleClose}>Close</Button>
                    </DialogActions>
                )}
            </Dialog>
        </>
    );
};

export default AddCustomTabDialog;
